import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { FileText, X, Download, Loader2, CheckCircle2, AlertTriangle, Info } from "lucide-react";

export const MAX_SOURCE_MB = 25;
export const SUPPORTED_SOURCE_TEXT = "PDF · DOCX · TXT · MD · CSV · PNG · JPG · WEBP · GIF — up to 25 MB each";
export const SOURCE_ACCEPT = ".pdf,.docx,.txt,.md,.csv,.png,.jpg,.jpeg,.webp,.gif";

export function isImage(f) { return ["png", "jpg", "jpeg", "webp", "gif"].includes((f?.file_type || "").toLowerCase()); }
export function isPdf(f) { return (f?.file_type || "").toLowerCase() === "pdf"; }
export function sourceLabel(f) { return f?.display_label || f?.file_name || "File"; }

const STATUS = {
  uploading: { label: "Uploading", cls: "text-slate-300 border-slate-600 bg-slate-500/10", Icon: Loader2, spin: true },
  reading: { label: "Reading file", cls: "text-cyan-200 border-cyan-500/40 bg-cyan-500/10", Icon: Loader2, spin: true },
  ready: { label: "Ready", cls: "text-emerald-200 border-emerald-500/40 bg-emerald-500/10", Icon: CheckCircle2 },
  unreadable: { label: "Could not read", cls: "text-amber-200 border-amber-500/40 bg-amber-500/10", Icon: AlertTriangle },
  unsupported: { label: "Not read automatically", cls: "text-slate-300 border-slate-600 bg-slate-500/10", Icon: Info },
  failed: { label: "Upload failed", cls: "text-red-200 border-red-500/40 bg-red-500/10", Icon: AlertTriangle },
};

export function SourceStatusBadge({ status, testid }) {
  const s = STATUS[status] || STATUS.unsupported;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${s.cls}`} data-testid={testid}>
      <Icon size={10} className={s.spin ? "animate-spin" : ""} /> {s.label}
    </span>
  );
}

export function SourcePreviewDialog({ file, onClose }) {
  const { activeWorkspace } = useAuth();
  const [detail, setDetail] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let cleanup = null;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${activeWorkspace.id}/uploads/${file.id}`);
        if (cancelled) return;
        setDetail(data);
        if (isImage(file) || isPdf(file)) {
          const token = localStorage.getItem("fx_token");
          const r = await fetch(`${BACKEND_URL}/api/workspaces/${activeWorkspace.id}/uploads/${file.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
          if (r.ok) {
            const url = URL.createObjectURL(await r.blob());
            cleanup = url;
            setBlobUrl(url);
          }
        }
      } catch { toast.error("Could not load preview"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; if (cleanup) URL.revokeObjectURL(cleanup); };
  }, [file, activeWorkspace.id]);

  const doDownload = async () => {
    const token = localStorage.getItem("fx_token");
    const r = await fetch(`${BACKEND_URL}/api/workspaces/${activeWorkspace.id}/uploads/${file.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(await r.blob());
    a.download = file.file_name; a.click();
  };

  const status = detail?.extraction_status || file.extraction_status;
  const note = detail?.extraction_note || file.extraction_note;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-40 p-4" onClick={onClose}>
      <div className="fx-panel w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="preview-dialog">
        <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
          <div className="min-w-0">
            <div className="text-sm text-slate-100 truncate">{sourceLabel(file)}</div>
            <div className="text-[11px] text-slate-500 mono">{(file.size / 1024).toFixed(1)} KB · {file.file_type} · {new Date(file.created_at).toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-2">
            <SourceStatusBadge status={status} testid="preview-status" />
            <button onClick={doDownload} className="border border-white/10 hover:border-white/20 text-slate-200 text-sm px-3 py-1.5 rounded-lg inline-flex items-center gap-1" data-testid="preview-download"><Download size={14} />Download</button>
            <button onClick={onClose} className="text-slate-500 hover:text-white p-1" data-testid="preview-close"><X size={16} /></button>
          </div>
        </div>
        {note && (
          <div className="px-4 py-2 text-xs bg-amber-500/10 border-b border-amber-500/25 text-amber-200" data-testid="preview-note">{note}</div>
        )}
        <div className="flex-1 overflow-hidden grid lg:grid-cols-3">
          <div className="lg:col-span-2 bg-[#050816] overflow-y-auto fx-scrollbar p-6">
            {loading ? (
              <div className="text-center py-16"><Loader2 className="mx-auto animate-spin text-cyan-300" size={20} /><div className="text-xs text-slate-500 mt-2">Loading preview…</div></div>
            ) : isImage(file) && blobUrl ? (
              <img src={blobUrl} alt={sourceLabel(file)} className="max-w-full mx-auto rounded-md shadow-lg" data-testid="preview-image" />
            ) : isPdf(file) && blobUrl ? (
              <iframe src={blobUrl} className="w-full h-[70vh] bg-white rounded-md" title={sourceLabel(file)} data-testid="preview-pdf" />
            ) : detail?.text_preview ? (
              <pre className="whitespace-pre-wrap text-sm text-slate-200 leading-relaxed font-mono" data-testid="preview-text">{detail.text_preview}</pre>
            ) : (
              <div className="text-center py-16 text-sm text-slate-500">
                <FileText size={22} className="mx-auto mb-2 text-slate-600" />
                No inline preview available for this file type.
                <div className="mt-3"><button onClick={doDownload} className="fx-btn-primary text-white text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1"><Download size={14} />Download to view</button></div>
              </div>
            )}
          </div>
          <div className="border-l border-[#1E293B] p-4 fx-scrollbar overflow-y-auto bg-[#0A0F1F] space-y-5">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Extracted content</div>
              <div className="text-xs text-slate-400">
                {status === "ready" ? `${(detail?.extracted_chars ?? detail?.text_preview?.length ?? 0).toLocaleString()} characters available for generation.` : "No text is being sent to generation from this file."}
              </div>
            </div>
            <div>
              <div className="mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Used in documents</div>
              {detail?.used_in?.length ? (
                <div className="space-y-1">
                  {detail.used_in.map((d) => (
                    <Link key={d.id} to={`/app/documents/${d.id}`} onClick={onClose} className="block text-xs px-2 py-2 rounded hover:bg-white/5 border border-transparent hover:border-[#1E293B]" data-testid={`preview-used-${d.id}`}>
                      <div className="text-slate-100 truncate">{d.title}</div>
                      <div className="text-[10px] mono text-slate-500">{d.document_number} · {d.status}</div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-slate-500">Not referenced by any document yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Shared client-side pre-upload validation. Returns error string or null. */
export function validateSourceFile(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const allowed = SOURCE_ACCEPT.replace(/\./g, "").split(",");
  if (!allowed.includes(ext)) return `${file.name}: .${ext} files aren't supported. Supported formats: ${SUPPORTED_SOURCE_TEXT}`;
  if (file.size > MAX_SOURCE_MB * 1024 * 1024) return `${file.name} is ${(file.size / 1048576).toFixed(1)} MB. The maximum source file size is ${MAX_SOURCE_MB} MB.`;
  return null;
}
